// src/components/ModalCambioPlan.jsx — cambiar el plan de un cliente
//
// Cambia las dos tablas de tráfico del service-port: la velocidad nueva
// entra en caliente, sin reiniciar la ONT. OJO: la VLAN NO cambia (está
// apuntado en ESTADO-DEL-PROYECTO.md como pendiente de decidir).

import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Iconos } from "../componentes/base";
import ModalPasos, { leerRespuesta } from "../componentes/olt/ModalPasos";

export default function ModalCambioPlan({ cliente, onClose, onSuccess, showToast }) {
  const [planes, setPlanes] = useState([]);
  const [planSel, setPlanSel] = useState(null);
  const [estado, setEstado] = useState("reposo");
  const [salida, setSalida] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cliente) return;
    setPlanSel(null); setEstado("reposo"); setSalida([]); setError("");
    (async () => {
      try {
        const res = await authFetch("/api/planes/listar");
        const data = await res.json();
        if (data.ok) setPlanes(data.planes || []);
      } catch {
        showToast?.("Error cargando planes", "error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?.id]);

  // Un cliente elige un NIVEL DE SERVICIO, no la fila de un nodo: cada
  // nombre se enseña una sola vez. (Supone que un mismo nombre cuesta igual
  // en los dos nodos.)
  const planesUnicos = planes.filter((p, i) => planes.findIndex((q) => q.nombre === p.nombre) === i);

  async function ejecutar() {
    if (!planSel) return;
    setEstado("corriendo"); setSalida([]); setError("");
    try {
      const res = await authFetch("/api/clientes/cambiar_plan", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cliente_id: cliente.id, nuevo_plan: planSel.nombre }),
      });
      const data = await leerRespuesta(res);
      setSalida(Array.isArray(data?.outputs) ? data.outputs : []);
      if (!res.ok || !data?.ok) throw new Error(data?.mensaje || "No se pudo cambiar el plan.");
      showToast?.(data?.mensaje || "Plan actualizado", "success");
      onSuccess?.();
    } catch (e) {
      const msg = String(e?.message || e);
      setError(msg);
      showToast?.(`Error: ${msg}`, "error");
    } finally {
      setEstado("fin");
    }
  }

  if (!cliente) return null;
  const nombre = [cliente.nombre, cliente.apellidos].filter(Boolean).join(" ");

  return (
    <ModalPasos
      abierto
      titulo="Cambiar de plan"
      icono={Iconos.planes}
      ficha={[
        ["Cliente", `#${cliente.id}`],
        ["Nombre", nombre || "—"],
        ["Plan actual", cliente.plan || "—"],
        ["Pasa a", planSel ? `${planSel.nombre} · ${planSel.velocidad} Mbps` : "—"],
      ]}
      aviso="Cambia las dos tablas de tráfico del service-port: la velocidad nueva entra en caliente, sin reiniciar la ONT ni cortar la sesión. La VLAN se queda como está."
      verbo={planSel ? `Cambiar a ${planSel.nombre}` : "Elige un plan"}
      listo={Boolean(planSel)}
      estado={estado}
      salida={salida}
      error={error}
      mensaje={planSel ? `Plan cambiado a ${planSel.nombre} (${planSel.velocidad} Mbps).` : "Plan cambiado."}
      fases={[
        { rotulo: "Leer el service-port", coincide: /^display service-port/, cmds: ["display service-port (el del cliente)"] },
        { rotulo: "Cambiar la velocidad", coincide: /^service-port \d+/, cmds: ["service-port … inbound traffic-table index … outbound traffic-table index …"] },
        { rotulo: "Comprobar", coincide: /^display service-port/, cmds: ["display service-port (otra vez)"] },
        { rotulo: "Guardar en la base", app: true, detalle: `clientes.plan → ${planSel?.nombre || "…"}` },
      ]}
      onEjecutar={ejecutar}
      onCerrar={onClose}
    >
      <span className="mp-rotulo">Nuevo plan</span>
      <div className="mp-opciones">
        {planesUnicos.map((p) => {
          const actual = p.nombre === cliente.plan;
          return (
            <button
              key={p.id}
              type="button"
              className={`mp-opcion ${actual ? "mp-opcion--actual" : ""}`}
              aria-pressed={planSel?.id === p.id}
              disabled={actual}
              title={actual ? "Es el plan que ya tiene" : undefined}
              onClick={() => setPlanSel(p)}
            >
              <b>{p.nombre}{actual ? " · actual" : ""}</b>
              <span>{p.velocidad} Mbps · ${p.costo}</span>
            </button>
          );
        })}
      </div>
    </ModalPasos>
  );
}
